import { environment } from "../../../../environments/environment";

const BASE_URL = environment.apiUrl;

// console.log(`API Base URL: ${BASE_URL}`);

export const API_ENDPOINTS = {
  /* -------------------- AUTH -------------------- */
  auth: {
    register: `${BASE_URL}/auth/register/`,
    login: `${BASE_URL}/auth/login/`,
    requestOtp: `${BASE_URL}/auth/request-otp/`,
    verifyOtp: `${BASE_URL}/auth/verify-otp/`,
    resendOtp: `${BASE_URL}/auth/resend-otp/`,
    refresh: `${BASE_URL}/auth/refresh/`,
    logout: `${BASE_URL}/auth/logout/`,
    me: `${BASE_URL}/auth/me/`,

    /* Password Reset */
    requestPasswordReset: `${BASE_URL}/auth/password-reset/request/`,
    verifyPasswordResetOtp: `${BASE_URL}/auth/password-reset/verify/`,
    confirmPasswordReset: `${BASE_URL}/auth/password-reset/confirm/`,
  },

  /* -------------------- PLATFORM ADMIN -------------------- */
  platformAdmin: {
    dashboard: `${BASE_URL}/platform-admin/dashboard/`,
    users: `${BASE_URL}/platform-admin/users/`,
    businesses: `${BASE_URL}/platform-admin/businesses/`,
    salons: `${BASE_URL}/platform-admin/salons/`,
    userDetail: (id: string) => `${BASE_URL}/platform-admin/users/${id}/`,

    betaInvitations: `${BASE_URL}/platform-admin/beta-invitations/`,
    betaInvitationDetail: (id: string) =>
      `${BASE_URL}/platform-admin/beta-invitations/${encodeURIComponent(id)}/`,
    betaInvitationResend: (id: string) =>
      `${BASE_URL}/platform-admin/beta-invitations/${encodeURIComponent(id)}/resend/`,
    betaInvitationRevoke: (id: string) =>
      `${BASE_URL}/platform-admin/beta-invitations/${encodeURIComponent(id)}/revoke/`,

    registrationSettings: `${BASE_URL}/platform-admin/registration-settings/`,

    billing: {
      overview: `${BASE_URL}/platform-admin/billing/overview/`,
      businesses: `${BASE_URL}/platform-admin/billing/businesses/`,
      invoices: `${BASE_URL}/platform-admin/billing/invoices/`,
      invoiceDetail: (id: string) =>
        `${BASE_URL}/platform-admin/billing/invoices/${encodeURIComponent(id)}/`,

      invoicePreview: `${BASE_URL}/platform-admin/billing/invoices/preview/`,

      invoiceGenerate: `${BASE_URL}/platform-admin/billing/invoices/generate/`,

      invoiceIssue: (id: string) =>
        `${BASE_URL}/platform-admin/billing/invoices/${encodeURIComponent(id)}/issue/`,

      invoiceVoid: (id: string) =>
        `${BASE_URL}/platform-admin/billing/invoices/${encodeURIComponent(id)}/void/`,

      invoiceAudit: (id: string) =>
        `${BASE_URL}/platform-admin/billing/invoices/${encodeURIComponent(id)}/audit/`,

      invoicePdf: (id: string) =>
        `${BASE_URL}/platform-admin/billing/invoices/${encodeURIComponent(id)}/pdf/`,

      businessSettings: (businessId: string) =>
        `${BASE_URL}/platform-admin/billing/businesses/${businessId}/settings/`,

      runs: `${BASE_URL}/platform-admin/billing/runs/`,

      runDetail: (runId: string) =>
        `${BASE_URL}/platform-admin/billing/runs/${runId}/`,
    },
  },

  /* -------------------- SALONS -------------------- */
  salons: {
    list: `${BASE_URL}/salons/`,
    create: `${BASE_URL}/salons/`,
    update: (pk: string) => `${BASE_URL}/salons/${pk}/`,
    detail: (pk: string) => `${BASE_URL}/salons/${pk}/`,
    delete: (pk: string) => `${BASE_URL}/salons/${pk}/`,

    services: {
      create: (salonId: string) => `${BASE_URL}/salons/${salonId}/services/`,
      list: (salonId: string) => `${BASE_URL}/salons/${salonId}/services/`,
      servicesBulkUpsert: (salonId: string) =>
        `${BASE_URL}/salons/${salonId}/services/bulk/`,
      deleteService: (salonId: string, serviceId: string) =>
        `${BASE_URL}/salons/${salonId}/services/${serviceId}/`,
    },

    staff: {
      list: (salonId: string) => `${BASE_URL}/salons/${salonId}/staff/`,
      create: (salonId: string) => `${BASE_URL}/salons/${salonId}/staff/`,
    },
  },

  /* -------------------- STAFF -------------------- */
  staff: {
    listAcrossBusiness: `${BASE_URL}/staff/`,
    listAcrossSalon: (salonId: string) =>
      `${BASE_URL}/salons/${salonId}/staff/`,
    detail: (userId: string) => `${BASE_URL}/staff/${userId}/`,
    createEmployee: (salonId: string) => `${BASE_URL}/salons/${salonId}/staff/`,
    updateEmployee: (userId: string) => `${BASE_URL}/staff/${userId}/`,
  },

  /* -------------------- STAFF AVAILABILITY / ASSISTANT -------------------- */
  staffAvailability: {
    /* Staff ↔ Services */
    services: (salonId: string, staffUserId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/services/`,

    serviceDetail: (salonId: string, staffUserId: string, mappingId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/services/${mappingId}/`,

    /* Weekly Working Hours */
    workingHoursList: (salonId: string, staffUserId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/working-hours/`,

    workingHoursUpsert: (salonId: string, staffUserId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/working-hours/upsert/`,

    workingHourDetail: (salonId: string, staffUserId: string, hourId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/working-hours/${hourId}/`,

    /* Time Off */
    timeOffList: (salonId: string, staffUserId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/time-off/`,

    createTimeOff: (salonId: string, staffUserId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/time-off/`,

    timeOffDetail: (salonId: string, staffUserId: string, timeoffId: string) =>
      `${BASE_URL}/assistant/salons/${salonId}/staff/${staffUserId}/time-off/${timeoffId}/`,
  },

  /* -------------------- PUBLIC SALONS (CUSTOMERS) -------------------- */
  public: {
    salons: `${BASE_URL}/public/salons/`,

    salonServices: (salonId: string) =>
      `${BASE_URL}/public/salons/${salonId}/services/`,

    salonServicesPagination: (salonId: string, page: number) =>
      `${BASE_URL}/public/salons/${salonId}/services/?page=${page}`,

    salonCatalog: (salonId: string) =>
      `${BASE_URL}/public/salons/${salonId}/catalog/`,

    /*
     * Stage 1: effective, bookable payment methods for a salon.
     * Card is only advertised once Square charging is live.
     */
    salonPaymentMethods: (salonId: string) =>
      `${BASE_URL}/public/salons/${salonId}/payment-methods/`,

    staffAvailability: (
      salonId: string,
      staffId: string,
      date: string,
      serviceIds: string,
    ) =>
      `${BASE_URL}/public/salons/${salonId}/availability/` +
      `?staff_id=${encodeURIComponent(staffId)}` +
      `&date=${encodeURIComponent(date)}` +
      `&service_ids=${encodeURIComponent(serviceIds)}`,

    searchAvailability: `${BASE_URL}/public/salons/search-availability/`,
  },

  /* -------------------- CUSTOMER -------------------- */
  customer: {
    favoritesSalons: {
      list: `${BASE_URL}/favorites/`,
      toggle: (salonId: string) => `${BASE_URL}/favorites/toggle/${salonId}/`,
    },

    appointments: {
      list: `${BASE_URL}/customer/appointments/`,
      detail: (appointmentId: string) =>
        `${BASE_URL}/customer/appointments/${appointmentId}/`,
      createForSalon: (salonId: string) =>
        `${BASE_URL}/customer/salons/${salonId}/appointments/`,
    },

    dashboard: {
      detail: `${BASE_URL}/customer/dashboard/`,
    },

    review: {
      addReview: `${BASE_URL}/customer/review/`,
    },

    promoCode: {
      list: `${BASE_URL}/promotions/`,
      redeemVoucher: (_code?: string) => `${BASE_URL}/promotions/redeem/`,
      validatePromo: `${BASE_URL}/promotions/validate/`,
      getUserRedeemedPromos: `${BASE_URL}/promotions/my-redemptions/`,
    },
  },

  /* -------------------- OWNER -------------------- */
  owner: {
    appointments: {
      list: `${BASE_URL}/owner/appointments/`,
      detail: (appointmentId: string) =>
        `${BASE_URL}/owner/appointments/${appointmentId}/`,
      updateStatus: (appointmentId: string) =>
        `${BASE_URL}/owner/appointments/${appointmentId}/status/`,
      createForSalon: (salonId: string) =>
        `${BASE_URL}/owner/salons/${salonId}/appointments/`,
    },

    analytics: {
      overview: `${BASE_URL}/analytics/dashboard/`,
      orders: `${BASE_URL}/business/orders/dashboard/`,
    },

    salons: {
      dropdowns: (salonId: string) =>
        `${BASE_URL}/owner/salons/${salonId}/dropdowns/`,
    },

    customers: {
      list: `${BASE_URL}/business/customers/`,
      create: `${BASE_URL}/customers/`,
      update: (id: string) => `${BASE_URL}/customers/by-customer/${id}/`,
      details: (id: string) => `${BASE_URL}/customers/${id}/`,
    },

    promoCode: {
      list: `${BASE_URL}/promotions/`,
      create: `${BASE_URL}/promotions/`,
      update: (promoId: string) => `${BASE_URL}/promotions/${promoId}/`,
      delete: (promoId: string) => `${BASE_URL}/promotions/${promoId}/`,
      getDetails: (promoId: string) => `${BASE_URL}/promotions/${promoId}/`,
      viewRedeemed: `${BASE_URL}/redemptions/`,
      toggleStatus: (promoId: string) =>
        `${BASE_URL}/promotions/${promoId}/toggle-status/`,
      redemptionsForPromo: (promoId: string) =>
        `${BASE_URL}/promotions/${promoId}/redemptions/`,
    },

    accountSettings: {
      userDetails: {
        getUserDetails: `${BASE_URL}/business/account/my-details/`,

        update: `${BASE_URL}/business/account/my-details/`,

        updateUserProfileimg: `${BASE_URL}/business/account/my-details/photo/`,

        deleteUserProfileimg: `${BASE_URL}/business/account/my-details/photo/`,
      },

      store: {
        getStoreDetails: `${BASE_URL}/business/account/store/`,

        update: `${BASE_URL}/business/account/store/`,
      },

      permissions: `${BASE_URL}/business/account/permissions/`,

      notifications: `${BASE_URL}/business/account/notifications/`,

      password: `${BASE_URL}/business/account/password/`,

      billing: `${BASE_URL}/business/account/billing/`,

      /*
       * Trusted Square Web Payments SDK configuration for the Billing
       * tab (Flow A — the salon pays gloget platform commissions).
       * Returns the PUBLIC platform app_id / location_id + environment;
       * never a secret. Fail-closed: configured=false names the exact
       * missing platform setting.
       */
      squareSdkConfig: `${BASE_URL}/business/account/billing/square-sdk-config/`,

      /*
       * Stage 1: salon payment-method settings (owner/staff only).
       * GET returns the effective policy (defaults when unconfigured).
       * PATCH saves the owner's explicit choice for that salon.
       */
      paymentSettings: (salonId: string) =>
        `${BASE_URL}/salons/${salonId}/payment-settings/`,

      billingCards: `${BASE_URL}/business/account/billing/cards/`,

      billingCardRemove: (cardId: string) =>
        `${BASE_URL}/business/account/billing/cards/${encodeURIComponent(cardId)}/`,

      billingCardDefault: (cardId: string) =>
        `${BASE_URL}/business/account/billing/cards/${encodeURIComponent(cardId)}/default/`,

      billingAuthorization: `${BASE_URL}/business/account/billing/authorization/`,

      invoiceDetail: (invoiceId: string) =>
        `${BASE_URL}/business/account/billing/invoices/${invoiceId}/`,

      invoicePdf: (invoiceId: string) =>
        `${BASE_URL}/business/account/billing/invoices/${invoiceId}/pdf/`,
    },
  },

  /* -------------------- SALON APPOINTMENTS -------------------- */
  salonAppointments: {
    listForSalon: (salonId: string) =>
      `${BASE_URL}/salons/${salonId}/appointments/`,
  },

  /* -------------------- OWNER APPOINTMENTS (LEGACY HELPER) -------------------- */
  OwnerAppointments: {
    listForAppointments: () => `${BASE_URL}/owner/appointments/`,
    updateStatus: (appointmentId: string) =>
      `${BASE_URL}/owner/appointments/${appointmentId}/status/`,
    createAppointment: (salonId: string) =>
      `${BASE_URL}/owner/salons/${salonId}/appointments/`,
  },

  /* -------------------- NOTIFICATIONS -------------------- */
  notification: {
    list: `${BASE_URL}/notifications/`,
    unreadCount: `${BASE_URL}/notifications/unread-count/`,
    markAllAsRead: `${BASE_URL}/notifications/mark-all-as-read/`,
    clearAll: `${BASE_URL}/notifications/clear-all/`,
    preferences: `${BASE_URL}/notifications/preferences/`,
    markAsRead: (notificationId: string) =>
      `${BASE_URL}/notifications/${notificationId}/mark-as-read/`,
    delete: (notificationId: string) =>
      `${BASE_URL}/notifications/${notificationId}/`,
  },

  /* -------------------- REVIEWS -------------------- */
  review: {
    list: `${BASE_URL}/business/reviews/`,
    addReview: `${BASE_URL}/customer/review/`,
    getSalonReview: `${BASE_URL}/customer/reviews/`,
    getCustomerReview: (salonId: string) =>
      `${BASE_URL}/salon/${salonId}/reviews/`,
  },
};
